import { CommonModule } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { Component, OnInit } from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import DOMPurify from "dompurify";
import { marked } from "marked";
import { MatIconModule } from "@angular/material/icon";
import { SpinnerComponent } from "../shared/spinner/spinner.component";

export interface ChapterMeta {
  folderId: number;
  title: string;
}

@Component({
  selector: "app-user-guide",
  imports: [CommonModule, MatIconModule, SpinnerComponent],
  templateUrl: "./user-guide.component.html",
  styleUrls: ["./user-guide.component.css"],
})
export class UserGuideComponent implements OnInit {
  chapters: ChapterMeta[] = [];
  activeChapter: ChapterMeta | null = null;
  renderedContent: SafeHtml = "";
  isLoading = false;
  chapterListError = false;
  private requestId = 0;

  constructor(private http: HttpClient, private sanitizer: DomSanitizer) {}

  ngOnInit(): void {
    this.http.get<ChapterMeta[]>("assets/user-guide/chapters.json").subscribe({
      next: (chapters) => {
        this.chapters = chapters;
        if (chapters.length > 0) {
          this.loadChapter(chapters[0]);
        }
      },
      error: () => {
        this.chapterListError = true;
      },
    });
  }

  onContentClick(event: MouseEvent): void {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const anchor = target.closest("a");
    if (!anchor) return;

    const match = (anchor.getAttribute("href") ?? "").match(/^chapter:(\d+)$/);
    if (!match) return;

    event.preventDefault();
    const chapter = this.chapters.find((item) => item.folderId === Number(match[1]));
    if (chapter) this.loadChapter(chapter);
  }

  loadChapter(chapter: ChapterMeta): void {
    if (this.activeChapter?.folderId === chapter.folderId && !this.isLoading) return;

    this.activeChapter = chapter;
    this.isLoading = true;
    const requestId = ++this.requestId;
    const markdownPath = `assets/user-guide/${chapter.folderId}/content.md`;

    this.http.get(markdownPath, { responseType: "text" }).subscribe({
      next: (markdownText) => {
        if (requestId !== this.requestId) return;

        const html = marked.parse(markdownText) as string;
        DOMPurify.addHook("uponSanitizeAttribute", (_node, data) => {
          if (data.attrName === "href" && /^chapter:\d+$/.test(data.attrValue)) {
            data.forceKeepAttr = true;
          }
        });
        let clean: string;
        try {
          clean = DOMPurify.sanitize(html, { ADD_ATTR: ["width", "height"] });
        } finally {
          DOMPurify.removeHook("uponSanitizeAttribute");
        }
        this.renderedContent = this.sanitizer.bypassSecurityTrustHtml(clean);
        this.isLoading = false;
      },
      error: () => {
        if (requestId !== this.requestId) return;

        this.activeChapter = null;
        this.renderedContent = this.sanitizer.bypassSecurityTrustHtml(
          '<p class="text-danger">Could not load chapter content.</p>'
        );
        this.isLoading = false;
      },
    });
  }
}